import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type PortalSwitchSize = 'sm' | 'lg' | null;

/**
 * PortalUI form switch — thin liquid-glass track, primary on-state.
 * Prefer this over raw `form-check form-switch` so pages share one switch language.
 *
 * @example
 * ```html
 * <app-portal-switch
 *   [(ngModel)]="active"
 *   labelOn="Aan"
 *   labelOff="Uit"
 *   ariaLabel="Actief">
 * </app-portal-switch>
 * ```
 */
@Component({
  selector: 'app-portal-switch',
  templateUrl: './portal-switch.component.html',
  styleUrls: ['./portal-switch.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PortalSwitchComponent),
      multi: true,
    },
  ],
})
export class PortalSwitchComponent implements ControlValueAccessor {
  @Input() size: PortalSwitchSize = null;
  /** Optional id for the input; generated when unset. */
  @Input() inputId: string | null = null;
  @Input() ariaLabel: string | null = null;
  @Input() title: string | null = null;
  /** Visible label when checked (e.g. Aan / On). */
  @Input() labelOn: string | null = null;
  /** Visible label when unchecked (e.g. Uit / Off). */
  @Input() labelOff: string | null = null;
  /** Static label (overrides on/off when set). */
  @Input() label: string | null = null;
  @Input({ transform: booleanAttribute }) disabled = false;
  @Input() extraClass: string | string[] | null = null;

  /** Two-way without ngModel: `[checked]` + `(changed)`. */
  @Input({ transform: booleanAttribute }) checked = false;
  @Output() changed = new EventEmitter<boolean>();

  @HostBinding('class.portal-switch-host')
  readonly hostClass = true;

  private onChange: (value: boolean) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private cvaDisabled = false;

  private static nextId = 0;
  readonly fallbackId = `portal-switch-${++PortalSwitchComponent.nextId}`;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  get resolvedId(): string {
    return this.inputId?.trim() || this.fallbackId;
  }

  get isDisabled(): boolean {
    return this.disabled || this.cvaDisabled;
  }

  get visibleLabel(): string | null {
    if (this.label != null && this.label !== '') {
      return this.label;
    }
    if (this.checked) {
      return this.labelOn;
    }
    return this.labelOff;
  }

  get switchClasses(): string[] {
    const classes = ['form-check', 'form-switch', 'portal-switch'];
    if (this.size === 'sm') {
      classes.push('portal-switch--sm');
    } else if (this.size === 'lg') {
      classes.push('portal-switch--lg');
    }
    if (typeof this.extraClass === 'string' && this.extraClass.trim()) {
      classes.push(...this.extraClass.trim().split(/\s+/));
    } else if (Array.isArray(this.extraClass)) {
      classes.push(...this.extraClass.filter(Boolean));
    }
    return classes;
  }

  writeValue(value: boolean | null | undefined): void {
    this.checked = !!value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  onInputChange(event: Event): void {
    const next = !!(event.target as HTMLInputElement)?.checked;
    this.checked = next;
    this.onChange(next);
    this.onTouched();
    this.changed.emit(next);
    this.cdr.markForCheck();
  }
}
